/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import serialize from 'form-serialize';
import cn from 'classnames';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';

import messenger from 'js/utils/messenger';
import apiHelper from 'js/api-helper';
import useValidationLogic from './use-validation-logic';

import Button from 'components/button';
import ValidationErrorTexts from 'components/validation-error-texts';

const isNotEmpty = value => !!value;

const maybeCastBoolean = value =>
  value === 'true' ? true : value === 'false' ? false : value;

const serializeForm = form => {
  const data = serialize(form, { hash: true, empty: true });

  return Object.entries(data).reduce((accum, [key, value]) => {
    const newValue = Array.isArray(value)
      ? value.filter(isNotEmpty)
      : maybeCastBoolean(value);

    return newValue === '' ? accum : { ...accum, [key]: newValue };
  }, {});
};

const Context = React.createContext({
  attachInput: () => {},
  detachInput: () => {},
  dirtyFields: {},
  errors: {},
  onChange: () => {}
});

const InnerForm = props => {
  const {
    children,
    className,
    endpoint,
    fieldNameMapping,
    id,
    onChange,
    onRef,
    onResponse,
    onSubmit,
    showSubmitButton,
    submitLabel,
    validationErrors,
    validationEndpoint,
    recaptchaSiteKey
  } = props;

  const [form, setForm] = React.useState();
  const [isLoading, setLoading] = React.useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    attachInput,
    detachInput,
    dirtyFields,
    errors,
    onInputChange,
    setErrors,
    setAllFieldsDirty,
    validateForm
  } = useValidationLogic(form, validationErrors);

  const submitToApi = React.useCallback(
    data => {
      onSubmit(data);
      return apiHelper
        .execute(endpoint, data)
        .then(res => {
          onResponse(res);
        })
        .catch(() => {
          onResponse({});
        })
        .finally(() => {
          if (isLoading) {
            setLoading(false);
          }
        });
    },
    [endpoint, onSubmit, onResponse]
  );
  const submitValidation = data => {
    apiHelper
      .execute(validationEndpoint, data)
      .then(validations => {
        if (Object.keys(validations).length === 0) {
          submitToApi(data);
          return;
        }

        // If fieldNameMapping is provided, map the validation keys back to the form field names
        // before setting the errors, so that the errors show up on the correct fields.
        // This is needed when the form field names are different from what the API expects.
        if (fieldNameMapping) {
          const reverseMappedValidations = {};
          Object.entries(validations).forEach(([key, value]) => {
            const originalName = Object.keys(fieldNameMapping).find(
              oldName => fieldNameMapping[oldName] === key
            );
            reverseMappedValidations[originalName || key] = value;
          });
          setErrors(reverseMappedValidations);
        } else {
          setErrors(validations);
        }

        setAllFieldsDirty();

        return;
      })
      .finally(() => setLoading(false));
  };

  const onFormSubmit = React.useCallback(
    async e => {
      e && e.preventDefault();

      messenger.hideMessage();

      const isValid = validateForm();

      if (!isValid) {
        setAllFieldsDirty();

        return;
      }

      const data = serializeForm(form);

      // If fieldNameMapping is provided, map the field names in the data back to what the API expects before submitting
      if (fieldNameMapping) {
        Object.entries(fieldNameMapping).forEach(([oldName, newName]) => {
          if (data[oldName] !== undefined) {
            data[newName] = data[oldName];
            delete data[oldName];
          }
        });
      }

      if (recaptchaSiteKey && executeRecaptcha) {
        try {
          const token = await executeRecaptcha('submit_form');
          data.recaptchaToken = token;
        } catch (err) {
          console.warn('Recaptcha error', err);
          return;
        }
      }

      setLoading(true);
      validationEndpoint ? submitValidation(data) : submitToApi(data);
    },
    [
      form,
      fieldNameMapping,
      validateForm,
      setErrors,
      setAllFieldsDirty,
      submitToApi,
      executeRecaptcha,
      recaptchaSiteKey
    ]
  );

  // NOTE: Yes, this is dirty. As far as I can tell this is the only way of exposing the submit function while still having it be preventable.
  React.useEffect(() => {
    onRef({ submit: onFormSubmit });
  }, [onRef, onFormSubmit]);

  return (
    <form
      action={endpoint}
      className={cn('form', className)}
      ref={setForm}
      id={id}
      onSubmit={onFormSubmit}
    >
      <Context.Provider
        value={{
          attachInput,
          detachInput,
          dirtyFields,
          errors,
          onChange: name => {
            onInputChange(name);

            onChange(serializeForm(form));
          }
        }}
      >
        {typeof children === 'function' ? children({ isLoading }) : children}

        {showSubmitButton && (
          <Button className="form-submit" type="submit">
            {submitLabel}
          </Button>
        )}
      </Context.Provider>
    </form>
  );
};

// eslint-disable-next-line react/no-multi-comp
const Form = props => {
  if (props.recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={props.recaptchaSiteKey}>
        <InnerForm {...props} />
      </GoogleReCaptchaProvider>
    );
  }

  return <InnerForm {...props} />;
};

Form.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  endpoint: PropTypes.string,
  fieldNameMapping: PropTypes.shape({ [PropTypes.string]: PropTypes.string }),
  id: PropTypes.string,
  onChange: PropTypes.func,
  onRef: PropTypes.func,
  onResponse: PropTypes.func,
  onSubmit: PropTypes.func,
  showSubmitButton: PropTypes.bool,
  submitLabel: PropTypes.string,
  validationErrors: PropTypes.exact(ValidationErrorTexts.propTypes),
  validationEndpoint: PropTypes.string,
  recaptchaSiteKey: PropTypes.string
};

Form.defaultProps = {
  validationErrors: {},
  onChange: () => {},
  onRef: () => {},
  onResponse: () => {},
  onSubmit: () => {}
};

Form.propTypesMeta = {
  children: 'exclude',
  className: 'exclude',
  id: 'exclude',
  showSubmitButton: 'exclude'
};

InnerForm.propTypes = Form.propTypes;
InnerForm.defaultProps = Form.defaultProps;

Form.Context = Context;

export default Form;
